import styles from "./DataTable.module.css";
import { EmptyState } from "./EmptyState";
import { Spinner } from "./Spinner";

const TONE_CLASS = { danger: styles.rowDanger, warning: styles.rowWarning };

/**
 * columns: [{ key, header, render?(row) }]
 * rowKey: the property holding a row's identity, or a FUNCTION deriving one.
 *   A grouped or aggregated table has no id column -- P&L rows are identified
 *   by year AND season -- and passing `(r) => ...` used to be accepted
 *   silently and then read as `keyOf(row)`, which is undefined for every
 *   row. React then keyed the whole table `undefined` (reordering reuses the
 *   wrong row's DOM) and selection compared undefined to undefined, so
 *   ticking one box would have ticked all of them.
 * selection: { selectedIds: Set, onToggle(id), onToggleAll(), idKey } -- omit to disable checkboxes
 * rowTone: optional (row) => "danger" | "warning" | null, for marking rows
 *   that need attention (#206). Styling only -- the caller is still expected
 *   to say why in a cell, since a tint alone is not readable in sunlight or
 *   to a colour-blind user.
 */
export function DataTable({ columns, rows, rowKey = "id", loading, emptyTitle = "Nothing here yet", emptyDescription, emptyAction, selection, onRowClick, rowTone }) {
  if (loading) return <Spinner />;
  if (!rows || rows.length === 0) return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />;

  const keyOf = typeof rowKey === "function" ? rowKey : (r) => r[rowKey];

  const allSelected = selection && rows.length > 0 && rows.every((r) => selection.selectedIds.has(keyOf(r)));

  return (
    <div className={styles.scrollWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            {selection && (
              <th className={styles.checkboxCell}>
                <input
                  type="checkbox"
                  aria-label="Select all rows"
                  checked={allSelected}
                  onChange={() => selection.onToggleAll(rows.map((r) => keyOf(r)))}
                  className={styles.checkbox}
                />
              </th>
            )}
            {columns.map((col) => (
              <th key={col.key}>{col.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={keyOf(row)}
              className={[onRowClick ? styles.clickable : null, TONE_CLASS[rowTone?.(row)]].filter(Boolean).join(" ") || undefined}
              onClick={() => onRowClick?.(row)}
            >
              {selection && (
                <td className={styles.checkboxCell} onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label={`Select row ${keyOf(row)}`}
                    checked={selection.selectedIds.has(keyOf(row))}
                    onChange={() => selection.onToggle(keyOf(row))}
                    className={styles.checkbox}
                  />
                </td>
              )}
              {columns.map((col) => (
                <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
